import React, { useState, useEffect } from 'react';
import { Search, SlidersHorizontal, ShoppingCart, Package, Grid, List, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../config/supabaseClient';
import { useCart } from '../context/CartContext';

const Catalog = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const productsPerPage = 9;

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Récupérer les produits depuis Supabase
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setProducts(data || []);
      setError(null);
    } catch (err) {
      console.error('Erreur lors de la récupération des produits:', err);
      setError('Impossible de charger les produits');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    'Tous',
    'Lessive',
    'Produit de ménage',
    'Liquide vaisselle',
    'Déodorant',
    'Hygiène corporelle',
    'Couche Pampers',
    'Cosmétique',
    'Serviette hygiénique',
    'Entretien du linge',
    'Entretien vaisselle',
    'Produit WC',
    'Entretien courant',
    'Entretien maison',
    'Divers bazar'
  ];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Tous' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch(sortBy) {
      case 'price-asc': return a.price - b.price;
      case 'price-desc': return b.price - a.price;
      case 'name': return a.name.localeCompare(b.name);
      default: return 0;
    }
  });

  const totalPages = Math.ceil(sortedProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = sortedProducts.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, sortBy]);

  // Scroll to top when the current page of products changes (pagination)
  useEffect(() => {
    // small timeout to let layout/transition settle
    const t = setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    return () => clearTimeout(t);
  }, [currentPage]);

  const styles = {
    container: {
      width: '100%',
      minHeight: '100vh',
      background: '#f8f9fa',
      paddingBottom: '3rem',
      overflowX: 'hidden',
    },
    hero: {
      background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
      padding: isMobile ? '2.5rem 1rem' : isTablet ? '3rem 2rem' : '4rem 2rem',
      color: '#ffffff',
      textAlign: 'center',
      width: '100%',
      boxSizing: 'border-box',
    },
    heroTitle: {
      fontSize: isMobile ? '1.8rem' : isTablet ? '2.5rem' : '3rem',
      fontWeight: '800',
      marginBottom: '1rem',
      fontFamily: "'Montserrat', sans-serif",
    },
    heroSubtitle: {
      fontSize: isMobile ? '0.95rem' : isTablet ? '1.1rem' : '1.2rem',
      color: '#cccccc',
      maxWidth: '700px',
      margin: '0 auto',
      lineHeight: '1.5',
      padding: '0 1rem',
    },
    mainContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isMobile ? '1.5rem 1rem' : isTablet ? '2rem 1.5rem' : '3rem 2rem',
      width: '100%',
      boxSizing: 'border-box',
    },
    topBar: {
      background: '#ffffff',
      padding: isMobile ? '1rem' : '1.5rem',
      borderRadius: isMobile ? '15px' : '20px',
      marginBottom: isMobile ? '1.5rem' : '2rem',
      boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
      width: '100%',
      boxSizing: 'border-box',
    },
    searchContainer: {
      display: 'flex',
      gap: '1rem',
      marginBottom: isMobile ? '1rem' : '1.5rem',
      flexDirection: isMobile ? 'column' : 'row',
    },
    searchWrapper: {
      flex: 1,
      position: 'relative',
    },
    searchInput: {
      width: '100%',
      padding: isMobile ? '0.9rem 0.9rem 0.9rem 2.8rem' : '1rem 1rem 1rem 3rem',
      border: '2px solid #e0e0e0',
      borderRadius: '12px',
      fontSize: isMobile ? '0.95rem' : '1rem',
      outline: 'none',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box',
    },
    searchIcon: {
      position: 'absolute',
      left: isMobile ? '0.8rem' : '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#666666',
    },
    filterButton: {
      background: 'linear-gradient(135deg, #c59b00 0%, #d4a800 100%)',
      color: '#000000',
      border: 'none',
      padding: '1rem 1.5rem',
      borderRadius: '12px',
      fontSize: isMobile ? '0.95rem' : '1rem',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap',
      justifyContent: 'center',
    },
    controlsBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
      flexWrap: 'wrap',
    },
    viewToggle: {
      display: 'flex',
      gap: '0.5rem',
    },
    viewButton: {
      background: '#f0f0f0',
      border: 'none',
      padding: '0.7rem',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    select: {
      padding: isMobile ? '0.6rem 0.8rem' : '0.7rem 1rem',
      border: '2px solid #e0e0e0',
      borderRadius: '8px',
      fontSize: isMobile ? '0.9rem' : '0.95rem',
      outline: 'none',
      cursor: 'pointer',
      background: '#ffffff',
      transition: 'all 0.3s ease',
    },
    resultsCount: {
      color: '#666666',
      fontSize: isMobile ? '0.85rem' : '0.95rem',
      fontWeight: '500',
    },
    contentWrapper: {
      display: 'flex',
      gap: isMobile ? '0' : '2rem',
      alignItems: 'flex-start',
      position: 'relative',
      width: '100%',
      boxSizing: 'border-box',
    },
    sidebar: {
      width: isMobile ? '85%' : isTablet ? '250px' : '280px',
      maxWidth: isMobile ? '320px' : 'none',
      background: '#ffffff',
      padding: isMobile ? '1.2rem' : '1.5rem',
      borderRadius: '20px',
      boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
      position: isMobile ? 'fixed' : 'sticky',
      top: isMobile ? 0 : '100px',
      left: isMobile ? '50%' : 'auto',
      transform: isMobile ? 'translateX(-50%)' : 'none',
      bottom: isMobile ? 0 : 'auto',
      zIndex: isMobile ? 1000 : 1,
      maxHeight: isMobile ? '100vh' : 'calc(100vh - 120px)',
      overflowY: 'auto',
      display: isMobile && !showFilters ? 'none' : 'block',
    },
    sidebarHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    sidebarTitle: {
      fontSize: isMobile ? '1.2rem' : '1.3rem',
      fontWeight: '700',
      color: '#000000',
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: '0.5rem',
      display: isMobile ? 'flex' : 'none',
      color: '#666666',
    },
    categoryList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    categoryItem: {
      padding: isMobile ? '0.7rem 0.9rem' : '0.8rem 1rem',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontSize: isMobile ? '0.9rem' : '0.95rem',
      fontWeight: '500',
      border: '2px solid transparent',
    },
    sectionTitle: {
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: '700',
      color: '#000000',
      marginTop: '1.5rem',
      marginBottom: '1rem',
    },
    productsGrid: {
      flex: 1,
      display: 'grid',
      gridTemplateColumns: viewMode === 'grid' 
        ? (isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)')
        : '1fr',
      gap: isMobile ? '1.2rem' : '1.5rem',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    productCard: {
      background: '#ffffff',
      borderRadius: isMobile ? '15px' : '20px',
      overflow: 'hidden',
      boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      border: '2px solid transparent',
      display: 'flex',
      flexDirection: viewMode === 'list' && !isMobile ? 'row' : 'column',
      cursor: 'pointer',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    productImageContainer: {
      position: 'relative',
      width: viewMode === 'list' && !isMobile ? '250px' : '100%',
      height: viewMode === 'list' && !isMobile ? '200px' : isMobile ? '220px' : '280px',
      overflow: 'hidden',
      flexShrink: 0,
    },
    productImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.3s ease',
    },
    productContent: {
      padding: isMobile ? '1rem' : '1.5rem',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    productCategory: {
      color: '#c59b00',
      fontSize: isMobile ? '0.8rem' : '0.85rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    productName: {
      fontSize: isMobile ? '1rem' : isTablet ? '1.15rem' : '1.3rem',
      fontWeight: '700',
      color: '#000000',
      marginBottom: '0.5rem',
      lineHeight: '1.3',
    },
    productQuantity: {
      color: '#666666',
      fontSize: isMobile ? '0.85rem' : '0.9rem',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    priceSection: {
      marginTop: 'auto',
    },
    price: {
      fontSize: isMobile ? '1.5rem' : '1.8rem',
      fontWeight: '800',
      color: '#c59b00',
      marginBottom: '1rem',
    },
    addToCartButton: {
      width: '100%',
      background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
      color: '#ffffff',
      border: 'none',
      padding: isMobile ? '0.9rem' : '1rem',
      borderRadius: '12px',
      fontSize: isMobile ? '0.9rem' : '1rem',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
    },
    overlay: {
      display: isMobile && showFilters ? 'block' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: isMobile ? '0.5rem' : '1rem',
      marginTop: '3rem',
      flexWrap: 'wrap',
    },
    pageButton: {
      background: '#ffffff',
      border: '2px solid #e0e0e0',
      padding: isMobile ? '0.6rem 0.9rem' : '0.7rem 1rem',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: isMobile ? '0.9rem' : '1rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.3rem',
      minWidth: isMobile ? '38px' : '42px',
      justifyContent: 'center',
    },
    pageNumber: {
      background: '#ffffff',
      border: '2px solid #e0e0e0',
      padding: isMobile ? '0.6rem 0.9rem' : '0.7rem 1rem',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: isMobile ? '0.9rem' : '1rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      minWidth: isMobile ? '38px' : '42px',
      textAlign: 'center',
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '5rem 2rem',
      background: '#ffffff',
      borderRadius: '20px',
    },
    spinAnimation: {
      animation: 'spin 1s linear infinite',
    },
  };

  const ProductCard = ({ product }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { addToCart } = useCart();

    return (
      <div
        style={{
          ...styles.productCard,
          transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
          boxShadow: isHovered ? '0 15px 40px rgba(0,0,0,0.15)' : '0 5px 20px rgba(0,0,0,0.08)',
          borderColor: isHovered ? '#c59b00' : 'transparent',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.productImageContainer}>
          <img
            src={product.image}
            alt={product.name}
            style={{
              ...styles.productImage,
              transform: isHovered ? 'scale(1.1)' : 'scale(1)',
            }}
          />
        </div>
        <div style={styles.productContent}>
          <div style={styles.productCategory}>{product.category}</div>
          <h3 style={styles.productName}>{product.name}</h3>
          <div style={styles.productQuantity}>
            <Package size={16} color="#666666" />
            {product.quantity}
          </div>
          <div style={styles.priceSection}>
            <div style={styles.price}>{product.price}€</div>
            <button
              style={{
                ...styles.addToCartButton,
                background: isHovered
                  ? 'linear-gradient(135deg, #c59b00 0%, #d4a800 100%)'
                  : 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
              }}
              onClick={() => addToCart(product)}
            >
              <ShoppingCart size={20} />
              Ajouter au panier
            </button>
          </div>
        </div>
      </div>
    );
  };

  const CategoryItem = ({ category }) => {
    const isSelected = selectedCategory === category;
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        style={{
          ...styles.categoryItem,
          background: isSelected ? '#c59b00' : isHovered ? '#f0f0f0' : 'transparent',
          color: isSelected ? '#ffffff' : '#000000',
          borderColor: isSelected ? '#c59b00' : 'transparent',
        }}
        onClick={() => {
          setSelectedCategory(category);
          if (isMobile) setShowFilters(false);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {category}
      </div>
    );
  };

  const Pagination = () => {
    const [hoveredPrev, setHoveredPrev] = useState(false);
    const [hoveredNext, setHoveredNext] = useState(false);
    const [hoveredPage, setHoveredPage] = useState(null);

    // Compact pagination: show a limited set of page buttons with ellipses
    const maxButtons = 5; // total numeric buttons to show (including current)
    const pageNumbers = [];
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      const half = Math.floor(maxButtons / 2);
      let start = Math.max(1, currentPage - half);
      let end = Math.min(totalPages, currentPage + half);

      if (currentPage <= half) {
        start = 1;
        end = maxButtons;
      }
      if (currentPage + half >= totalPages) {
        end = totalPages;
        start = totalPages - maxButtons + 1;
      }

      if (start > 1) {
        pageNumbers.push(1);
        if (start > 2) pageNumbers.push('left-ellipsis');
      }

      for (let i = start; i <= end; i++) pageNumbers.push(i);

      if (end < totalPages) {
        if (end < totalPages - 1) pageNumbers.push('right-ellipsis');
        pageNumbers.push(totalPages);
      }
    }

    return (
      <div style={styles.pagination}>
        <button
          style={{
            ...styles.pageButton,
            opacity: currentPage === 1 ? 0.5 : 1,
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            background: hoveredPrev && currentPage !== 1 ? '#c59b00' : '#ffffff',
            color: hoveredPrev && currentPage !== 1 ? '#ffffff' : '#000000',
            borderColor: hoveredPrev && currentPage !== 1 ? '#c59b00' : '#e0e0e0',
          }}
          onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          onMouseEnter={() => setHoveredPrev(true)}
          onMouseLeave={() => setHoveredPrev(false)}
        >
          <ChevronLeft size={isMobile ? 18 : 20} />
          {!isMobile && 'Précédent'}
        </button>

        {pageNumbers.map((p) => {
          if (p === 'left-ellipsis' || p === 'right-ellipsis') {
            return (
              <div key={p} style={{ ...styles.pageNumber, cursor: 'default' }}>...</div>
            );
          }

          return (
            <button
              key={p}
              style={{
                ...styles.pageNumber,
                background: currentPage === p ? '#c59b00' : hoveredPage === p ? '#f0f0f0' : '#ffffff',
                color: currentPage === p ? '#ffffff' : '#000000',
                borderColor: currentPage === p ? '#c59b00' : '#e0e0e0',
              }}
              onClick={() => setCurrentPage(p)}
              onMouseEnter={() => setHoveredPage(p)}
              onMouseLeave={() => setHoveredPage(null)}
            >
              {p}
            </button>
          );
        })}

        <button
          style={{
            ...styles.pageButton,
            opacity: currentPage === totalPages ? 0.5 : 1,
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            background: hoveredNext && currentPage !== totalPages ? '#c59b00' : '#ffffff',
            color: hoveredNext && currentPage !== totalPages ? '#ffffff' : '#000000',
            borderColor: hoveredNext && currentPage !== totalPages ? '#c59b00' : '#e0e0e0',
          }}
          onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          onMouseEnter={() => setHoveredNext(true)}
          onMouseLeave={() => setHoveredNext(false)}
        >
          {!isMobile && 'Suivant'}
          <ChevronRight size={isMobile ? 18 : 20} />
        </button>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.hero}>
        <h1 style={styles.heroTitle}>Notre Catalogue</h1>
        <p style={styles.heroSubtitle}>
          Découvrez toutes nos palettes de produits d'hygiène à prix imbattables
        </p>
      </div>

      <div style={styles.mainContent}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <Package size={64} color="#c59b00" style={{ marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.5rem', color: '#666666' }}>
              Chargement des produits...
            </h3>
          </div>
        ) : error ? (
          <div style={styles.loadingContainer}>
            <Package size={64} color="#ff0000" style={{ marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.5rem', color: '#ff0000', marginBottom: '1rem' }}>
              {error}
            </h3>
            <button
              onClick={fetchProducts}
              style={{
                background: 'linear-gradient(135deg, #c59b00 0%, #d4a800 100%)',
                color: '#000000',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
              }}
            >
              Réessayer
            </button>
          </div>
        ) : (
          <>
            <div style={styles.topBar}>
              <div style={styles.searchContainer}>
                <div style={styles.searchWrapper}>
                  <Search size={isMobile ? 18 : 20} style={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Rechercher un produit..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      ...styles.searchInput,
                      borderColor: searchTerm ? '#c59b00' : '#e0e0e0',
                    }}
                  />
                </div>
                {isMobile && (
                  <button
                    style={styles.filterButton}
                    onClick={() => setShowFilters(true)}
                  >
                    <SlidersHorizontal size={20} />
                    Filtres
                  </button>
                )}
              </div>

              <div style={styles.controlsBar}>
                <span style={styles.resultsCount}>
                  {sortedProducts.length} produit{sortedProducts.length > 1 ? 's' : ''}
                </span>

                {!isMobile && (
                  <div style={styles.viewToggle}>
                    <button
                      style={{
                        ...styles.viewButton,
                        background: viewMode === 'grid' ? '#c59b00' : '#f0f0f0',
                        color: viewMode === 'grid' ? '#ffffff' : '#000000',
                      }}
                      onClick={() => setViewMode('grid')}
                    >
                      <Grid size={20} />
                    </button>
                    <button
                      style={{
                        ...styles.viewButton,
                        background: viewMode === 'list' ? '#c59b00' : '#f0f0f0',
                        color: viewMode === 'list' ? '#ffffff' : '#000000',
                      }}
                      onClick={() => setViewMode('list')}
                    >
                      <List size={20} />
                    </button>
                  </div>
                )}

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={styles.select}
                >
                  <option value="featured">Par défaut</option>
                  <option value="price-asc">Prix croissant</option>
                  <option value="price-desc">Prix décroissant</option>
                  <option value="name">Nom A-Z</option>
                </select>
              </div>
            </div>

            <div style={styles.contentWrapper}>
              <div
                style={styles.overlay}
                onClick={() => setShowFilters(false)}
              />

              <aside style={styles.sidebar}>
                <div style={styles.sidebarHeader}>
                  <h3 style={styles.sidebarTitle}>Catégories</h3>
                  <button
                    style={styles.closeButton}
                    onClick={() => setShowFilters(false)}
                  >
                    <X size={24} />
                  </button>
                </div>

                <div style={styles.categoryList}>
                  {categories.map(category => (
                    <CategoryItem key={category} category={category} />
                  ))}
                </div>
              </aside>

              <div style={{ flex: 1, width: '100%' }}>
                <div style={styles.productsGrid}>
                  {currentProducts.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {currentProducts.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    background: '#ffffff',
                    borderRadius: '20px',
                    marginTop: '2rem',
                  }}>
                    <Package size={64} color="#cccccc" style={{ marginBottom: '1rem' }} />
                    <h3 style={{ fontSize: '1.5rem', color: '#666666', marginBottom: '0.5rem' }}>
                      Aucun produit trouvé
                    </h3>
                    <p style={{ color: '#999999' }}>
                      Essayez de modifier vos critères de recherche
                    </p>
                  </div>
                )}

                {totalPages > 1 && currentProducts.length > 0 && <Pagination />}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Catalog;